import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-5 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default:
          "border shadow-xs bg-white text-[hsl(var(--primary))] border-[hsl(var(--primary))] hover:bg-[color-mix(in_oklab,white_92%,hsl(var(--primary))_8%)] dark:bg-[hsl(var(--primary))] dark:text-[hsl(var(--primary-foreground))] dark:border-transparent dark:hover:bg-[color-mix(in_oklab,hsl(var(--primary))_92%,black_8%)]",
        primary:
          "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] shadow-xs hover:bg-[color-mix(in_oklab,hsl(var(--primary))_92%,black_8%)] focus-visible:ring-[color-mix(in_oklab,hsl(var(--primary))_35%,transparent)]",
        success:
          "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] shadow-xs hover:bg-[color-mix(in_oklab,hsl(var(--success))_92%,black_8%)] focus-visible:ring-[color-mix(in_oklab,hsl(var(--success))_35%,transparent)]",
        destructive:
          "bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))] shadow-xs hover:bg-[color-mix(in_oklab,hsl(var(--destructive))_92%,black_8%)] focus-visible:ring-[color-mix(in_oklab,hsl(var(--destructive))_35%,transparent)]",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50",
        secondary:
          "bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))] shadow-xs hover:bg-[color-mix(in_oklab,hsl(var(--secondary))_92%,black_8%)]",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        md: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5 [&_svg]:size-4",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4 [&_svg]:size-6",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }




